import { ReactNode } from 'react';

import { Text } from '../../../../cdl/Text/Text';
import { formatDateDelivery } from '../../../../common/helpers/formatDateDelivery.helper';
import { formatMoney } from '../../../../common/helpers/formatMoney.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { usePaymentTermsFormat } from '../../../../common/hooks/usePaymentTermsFormat';
import { Port } from '../../../../port/Port';
import { OfferModel } from '../../../model/OfferModel';
import { OfferHistoryCommercialField, OfferHistoryCoreField } from '../../history/buildOfferHistoryTransitions';
import { EmptyValue } from '../../history/components/EmptyValue';

export const useOfferHistoryFormatting = () => {
    const { getHumanReadableValue } = useEnums();
    const formatPaymentTerms = usePaymentTermsFormat();

    return {
        coreField: (field: OfferHistoryCoreField, offer: OfferModel): ReactNode => {
            switch (field) {
                case 'portId':
                    return offer.port ? (
                        <Port port={offer.port} vesselId={offer.vesselId} showTooltip />
                    ) : (
                        <EmptyValue />
                    );
                case 'agentId': {
                    const value = offer.agentInformation?.name;

                    return value ? <Text>{value}</Text> : <EmptyValue />;
                }
                case 'dateDelivery': {
                    const value = formatDateDelivery({ date: offer.dateDelivery });

                    return value ? <Text>{value}</Text> : <EmptyValue />;
                }
                case 'buyerReference':
                    return offer.buyerReference ? <Text>{offer.buyerReference}</Text> : <EmptyValue />;
                case 'vendorReference':
                    return offer.vendorReference ? <Text>{offer.vendorReference}</Text> : <EmptyValue />;
                case 'supplyMode': {
                    const value = offer.supplyMode
                        ? getHumanReadableValue('supplyMode', offer.supplyMode) ?? offer.supplyMode
                        : Characters.EN_DASH;

                    return value !== Characters.EN_DASH ? <Text>{value}</Text> : <EmptyValue />;
                }
                case 'noticeDays':
                    return offer.noticeDays !== null && offer.noticeDays !== undefined ? (
                        <Text>{offer.noticeDays}</Text>
                    ) : (
                        <EmptyValue />
                    );
                case 'paymentTerms': {
                    if (!offer.paymentTermReference || !offer.paymentTermReferenceDays) {
                        return <EmptyValue />;
                    }

                    return (
                        <Text>{formatPaymentTerms(offer.paymentTermReference, offer.paymentTermReferenceDays)}</Text>
                    );
                }
            }
        },
        commercialField: (field: OfferHistoryCommercialField, offer: OfferModel): ReactNode => {
            switch (field) {
                case 'volume':
                    return offer.volume !== null && offer.volume !== undefined ? (
                        <Text>{offer.volume}</Text>
                    ) : (
                        <EmptyValue />
                    );
                case 'ppl': {
                    return offer.ppl ? (
                        <Text>
                            {formatMoney({
                                value: offer.ppl.value,
                                currency: offer.ppl.currency,
                                maximumFractionDigits: 3,
                            })}
                        </Text>
                    ) : (
                        <EmptyValue />
                    );
                }
                case 'total': {
                    return offer.total ? (
                        <Text>
                            {formatMoney({
                                value: offer.total.value,
                                currency: offer.total.currency,
                                maximumFractionDigits: 2,
                            })}
                        </Text>
                    ) : (
                        <EmptyValue />
                    );
                }
            }
        },
    };
};
